import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import useForceUpdate from 'use-force-update';
import {mergeRight, propOr} from 'ramda';
import {Faders, CaretDoubleUp, CaretDoubleDown} from '@phosphor-icons/react';

import IconButton from '../internal_components/_IconButton.react';
import GlobalControl from '../internal_components/_GlobalControl.react';
import {relative_dimension} from './../utils.js';

/**
 * A layout component used for easily arranging groups of controls into a header.
 *
 * **Example Usage**
 * ```
 * app.layout = ddk.App([
 *     ddk.ControlsHeader(
 *         primary_controls=[
 *             dcc.Input(value="frontier", style={"width": "100%"}, name="Search"),
 *             dcc.Dropdown(
 *                 placeholder="City",
 *                 options=[
 *                     {"label": "New York City", "value": "NYC"},
 *                     {"label": "Montreal", "value": "MTL"},
 *                 ],
 *                 value="MTL",
 *             ),
 *             dcc.Dropdown(
 *                 placeholder="Driver",
 *                 options=[
 *                     {"label": i, "value": i}
 *                     for i in ["Rear", "Front", "Side"]
 *                 ],
 *                 multi=True,
 *                 value=["Rear", "Side"],
 *             ),
 *             dcc.Input(value="frontier", name="Search"),
 *             dcc.Slider(min=0, max=10, marks={0: "0", 5: "5", 10: "10"}, value=5),
 *         ],
 *         sticky=True,
 *     )
 * ]);
 * ```
 */
const ControlsHeader = props => {
    const {
        id,
        primary_controls,
        secondary_controls,
        sticky,
        padding,
        margin,
        background_color,
        border_width,
        border_style,
        border_color,
        border_radius,
        box_shadow,
    } = props;

    const forceUpdate = useForceUpdate();

    const handleThemeUpdate = () => {
        forceUpdate();
    };

    // State to track if the controls header is stickied to the top of the app
    const [isSticky, setIsSticky] = useState(false);
    // State to track whether secondary controls drawer is open or closed
    const [drawerOpen, setDrawerOpen] = useState(false);
    // State to track whether or not controls bar is hidden
    const [isHidden, setIsHidden] = useState(false);

    // Refs for Controls Header container and controls drawer container
    const containerRef = useRef(null);
    const globalControlsBarDrawerRef = useRef(null);

    // Toggle the controlsDrawer state when the button is clicked
    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
        const element = globalControlsBarDrawerRef.current;

        if (element) {
            if (drawerOpen) {
                element.style.overflow = 'hidden';
            } else {
                setTimeout(() => {
                    element.style.overflow = 'visible';
                }, 400);
            }
        }
    };

    // Set the isSticky state to true when the controls header is in view
    const callBackFunction = entries => {
        const [entry] = entries;
        setIsSticky(!entry.isIntersecting);
    };

    // Use an IntersectionObserver to determine if the controls header is in view.
    useEffect(() => {
        if (!sticky) {
            return () => {};
        }

        const observer = new IntersectionObserver(callBackFunction, {
            threshold: 1,
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, [sticky]);

    // Event listener to update style of the controls header when the theme changes
    useEffect(() => {
        window.addEventListener('dash-theme-update', handleThemeUpdate);
        return () => {
            // unsubscribe event
            window.removeEventListener('dash-theme-update', handleThemeUpdate);
        };
    }, []);

    // If width is less than 1, convert it to a percentage
    let width = props.width;
    if (width < 1) {
        width *= 100;
    }

    const style = mergeRight(
        {
            padding: padding,
            margin: margin,
            borderWidth: border_width,
            borderStyle: border_style,
            borderColor: border_color,
            borderRadius: border_radius,
            box_shadow: box_shadow,
            width: relative_dimension(
                parseFloat(width, 10),
                parseFloat(margin, 10)
            ),
        },
        propOr({}, 'style', props)
    );

    return (
        <div
            id={id}
            ref={containerRef}
            style={style}
            data-user-style={JSON.stringify(props.dataUserStyle || props.style)} // eslint-disable-line react/prop-types
            className={`controls-header-container ${
                isSticky ? 'sticky' : ''
            } ${propOr('', 'className', props)}`}
        >
            {((primary_controls && primary_controls.length > 0) ||
                (secondary_controls && secondary_controls.length > 0)) && (
                <div
                    className={`global-controls-bar ${isSticky ? 'stickied' : ''}`}
                >
                    <div
                        className={`global-controls-bar-content ${isHidden && 'hidden'}`}
                        style={{background: background_color}}
                    >
                        <div className="global-controls-bar-controls-container">
                            <div
                                className={`global-controls-bar-controls ${
                                    primary_controls.length > 3
                                        ? 'grid'
                                        : 'flex'
                                }`}
                            >
                                {primary_controls.map((child, index) => {
                                    return (
                                        <GlobalControl // TODO: Add `label` and `description` props to dash components and use those instead
                                            key={index}
                                            label={
                                                child.props.title ??
                                                child.props?._dashprivate_layout
                                                    ?.props?.title ??
                                                ' '
                                            }
                                            tooltip={
                                                child.props.description ??
                                                child.props?._dashprivate_layout
                                                    ?.props?.description
                                            }
                                        >
                                            {child}
                                        </GlobalControl>
                                    );
                                })}
                            </div>
                            {secondary_controls &&
                                secondary_controls.length > 0 && (
                                    <div
                                        className={`global-controls-bar-secondary-drawer ${
                                            drawerOpen && 'open'
                                        }`}
                                    >
                                        <div
                                            className="global-controls-bar-secondary-divider-wrapper"
                                            ref={globalControlsBarDrawerRef}
                                        >
                                            <div className="divider" />
                                            <div
                                                className={`global-controls-bar-controls ${
                                                    secondary_controls.length >
                                                    3
                                                        ? 'grid'
                                                        : 'flex'
                                                }`}
                                            >
                                                {secondary_controls.map(
                                                    (child, index) => {
                                                        return (
                                                            <GlobalControl
                                                                key={index}
                                                                label={
                                                                    child.props
                                                                        .title ??
                                                                    child.props
                                                                        ?._dashprivate_layout
                                                                        ?.props
                                                                        ?.title ??
                                                                    ' '
                                                                }
                                                                tooltip={
                                                                    child.props
                                                                        .description ??
                                                                    child.props
                                                                        ?._dashprivate_layout
                                                                        ?.props
                                                                        ?.description
                                                                }
                                                            >
                                                                {child}
                                                            </GlobalControl>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                        </div>
                        {secondary_controls && (
                            <div className="global-controls-bar-button-wrapper">
                                <IconButton
                                    icon={<Faders />}
                                    onClick={toggleDrawer}
                                />
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className={`global-controls-bar-hide-button ${
                            isSticky || isHidden ? 'stickied' : ''
                        }`}
                        onClick={() => {
                            setIsHidden(!isHidden);
                        }}
                    >
                        {isHidden ? (
                            <CaretDoubleDown size={20} />
                        ) : (
                            <CaretDoubleUp size={20} />
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

ControlsHeader.defaultProps = {
    padding: 0,
    margin: 0,
    width: 100,
    sticky: true,
};

ControlsHeader.propTypes = {
    /**
     * The ID of this component, used to identify Dash components
     * in callbacks. The ID needs to be unique across all of the
     * components in an app.
     */
    id: PropTypes.string,

    /**
     * The list of components that are children of the ControlsHeader container.
     */
    children: PropTypes.node,

    /**
     * These should be `dcc` control elements like `dcc.Dropdown`, `dcc.Input`,
     * `dcc.DatePickerSingle` or `htmlButton` that will appear in the primary
     * control area of the ControlsHeader.
     */
    primary_controls: PropTypes.node,

    /**
     * These should be `dcc` control elements like `dcc.Dropdown`, `dcc.Input`,
     * `dcc.DatePickerSingle` or `htmlButton` that will appear in the secondary
     * control drawer of the ControlsHeader.
     */
    secondary_controls: PropTypes.node,

    /**
     * Prop that controls whether or not the ControlsHeader will remain stickied to
     * the top of the app when the user has scrolled past it.
     */
    sticky: PropTypes.bool,

    /**
     * Number between 0 and 100 representing the width of the component
     * with respect to its parent.
     * - This is a percentage by default: `25` means take up 25% of the space.
     * - Unless <1, in which it represents a decimal: 0.25 is the same as 25
     *
     * Note that these units are different than the CSS `style` units where
     * `style={'width': 25}` means _25 pixels_, not 25%.
     */
    width: PropTypes.number,

    /**
     * Space (in pixels) surrounding the Controls Header.
     */
    margin: PropTypes.number,

    /**
     * Space (in pixels) on the inside of the Controls Header, between the border
     * and the edge of the content.
     */
    padding: PropTypes.number,

    /**
     * The background color applied to the ControlsHeader. Overrides theme.hero_controls_background_color.
     */
    background_color: PropTypes.string,

    /**
     * The box shadow(s) applied to the ControlsHeader.
     */
    box_shadow: PropTypes.string,

    /**
     * The border width applied to the ControlsHeader.
     */
    border_width: PropTypes.string,

    /**
     * The border style applied to the ControlsHeader.
     */
    border_style: PropTypes.string,

    /**
     * The border color applied to the ControlsHeader.
     */
    border_color: PropTypes.string,

    /**
     * The border radius applied to the ControlsHeader.
     */
    border_radius: PropTypes.string,

    /**
     * Optional additional CSS styles.
     * - If `width`, `padding`, or `margin` are supplied within `style`,
     * then this will override the component-level `width`, `padding`, or `margin`.
     */
    style: PropTypes.object,

    /**
     * Optional user-defined CSS class for the ControlsHeader container.
     */
    className: PropTypes.string,
};

export default ControlsHeader;
